// Web Audio API Sound Engine for Melodalis Kawaii Guitar & Sound Effects

class SoundEngine {
  private ctx: AudioContext | null = null;
  public isMuted: boolean = false;

  private initContext() {
    if (!this.ctx) {
      const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioCtx();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  // Plucks an acoustic guitar note given a frequency in Hz
  public playGuitarPluck(freq: number, duration: number = 2.2, volume: number = 0.5) {
    if (this.isMuted) return;
    try {
      this.initContext();
      if (!this.ctx) return;

      const now = this.ctx.currentTime;
      const masterGain = this.ctx.createGain();
      masterGain.gain.setValueAtTime(volume, now);
      masterGain.connect(this.ctx.destination);

      // Lowpass filter simulates the guitar wooden body resonance and damping
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(freq * 6, now);
      filter.frequency.exponentialRampToValueAtTime(Math.max(freq * 1.5, 120), now + duration * 0.7);
      filter.connect(masterGain);

      // Main fundamental oscillator (sawtooth/triangle blend for rich string timbre)
      const osc1 = this.ctx.createOscillator();
      osc1.type = 'triangle';
      osc1.frequency.setValueAtTime(freq, now);

      const oscGain1 = this.ctx.createGain();
      oscGain1.gain.setValueAtTime(0.7, now);
      oscGain1.gain.exponentialRampToValueAtTime(0.0001, now + duration);
      osc1.connect(oscGain1);
      oscGain1.connect(filter);

      // Second harmonic (rich warmth)
      const osc2 = this.ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(freq * 2, now);

      const oscGain2 = this.ctx.createGain();
      oscGain2.gain.setValueAtTime(0.35, now);
      oscGain2.gain.exponentialRampToValueAtTime(0.0001, now + duration * 0.7);
      osc2.connect(oscGain2);
      oscGain2.connect(filter);

      // Initial pluck impulse (subtle noise burst simulation)
      const pluckOsc = this.ctx.createOscillator();
      pluckOsc.type = 'sawtooth';
      pluckOsc.frequency.setValueAtTime(freq * 3, now);
      const pluckGain = this.ctx.createGain();
      pluckGain.gain.setValueAtTime(0.4, now);
      pluckGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
      pluckOsc.connect(pluckGain);
      pluckGain.connect(filter);

      osc1.start(now);
      osc2.start(now);
      pluckOsc.start(now);

      osc1.stop(now + duration);
      osc2.stop(now + duration);
      pluckOsc.stop(now + 0.1);
    } catch {
      // AudioContext autoplay restrictions or browser limitations
    }
  }

  // Strum multiple notes with realistic guitar strum timing delay
  public strumChord(frequencies: (number | null)[], speedMs: number = 35) {
    if (this.isMuted) return;
    const activeFreqs = frequencies.filter((f): f is number => f !== null && f > 0);
    activeFreqs.forEach((freq, idx) => {
      setTimeout(() => {
        this.playGuitarPluck(freq, 2.5, 0.45);
      }, idx * speedMs);
    });
  }

  // Kawaii Victory / Correct Chime
  public playSuccessSfx() {
    if (this.isMuted) return;
    try {
      this.initContext();
      if (!this.ctx) return;
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      const now = this.ctx.currentTime;
      notes.forEach((freq, i) => {
        if (!this.ctx) return;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + i * 0.09);
        gain.gain.setValueAtTime(0.2, now + i * 0.09);
        gain.gain.exponentialRampToValueAtTime(0.001, now + i * 0.09 + 0.35);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(now + i * 0.09);
        osc.stop(now + i * 0.09 + 0.4);
      });
    } catch {}
  }

  // Kawaii Gentle "Try Again" cartoon boing
  public playIncorrectSfx() {
    if (this.isMuted) return;
    try {
      this.initContext();
      if (!this.ctx) return;
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(320, now);
      osc.frequency.exponentialRampToValueAtTime(180, now + 0.3);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start(now);
      osc.stop(now + 0.35);
    } catch {}
  }

  // Kawaii Bubble Click
  public playPopSfx() {
    if (this.isMuted) return;
    try {
      this.initContext();
      if (!this.ctx) return;
      const now = this.ctx.currentTime;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(400, now);
      osc.frequency.exponentialRampToValueAtTime(800, now + 0.06);
      gain.gain.setValueAtTime(0.15, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.07);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start(now);
      osc.stop(now + 0.08);
    } catch {}
  }

  // Twinkling sparkle arpeggio
  public playSparkleSfx() {
    if (this.isMuted) return;
    try {
      this.initContext();
      if (!this.ctx) return;
      const pitches = [1046.5, 1318.5, 1567.98, 2093.0];
      const now = this.ctx.currentTime;
      pitches.forEach((freq, i) => {
        if (!this.ctx) return;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + i * 0.05);
        gain.gain.setValueAtTime(0.12, now + i * 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.05 + 0.25);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(now + i * 0.05);
        osc.stop(now + i * 0.05 + 0.3);
      });
    } catch {}
  }
}

export const soundEngine = new SoundEngine();

